/**
 * Assessor engagement workpapers — V102-7-3 / ADR-0059 (C3PAO), hardened by
 * the V102-7-5 security review (H1, H2, L5, L6).
 *
 * A third-party assessor records one workpaper per objective × asset on its
 * engagement (`cmmc_workpapers`): the assessment method (examine / interview /
 * test), typed evidence refs, the author and the author's engagement role. A
 * second engagement member reviews it; the review becomes a determination
 * THROUGH the determination service (`proposeDetermination` +
 * `reviewDetermination`), never a direct INSERT, so every determination
 * invariant holds: period of this workspace and not closed, objective known,
 * asset in this workspace, evidence refs typed and owned by this workspace,
 * SoD (reviewer ≠ proposer), the cell's previous reviewed row superseded in
 * the same batch.
 *
 * WHO MAY CALL THIS. Authority is the caller's job, and the only caller is the
 * firm tier (`FirmTierRepository`, ADR-0047): it proves accepted firm staff on
 * the engagement roster, no client hat, fresh firm MFA, an active
 * (not revoked, inside its window) engagement, and a non-revoked firm-type
 * authorization, then hands this service a `VerifiedAssessorEngagement`. The
 * service re-checks everything that is tenant data: the engagement's period,
 * the author's / reviewer's open role, and that the workpaper belongs to the
 * engagement in the URL.
 *
 * Roles per assessor type: `c3pao` uses lead_cca / cca; every other family
 * uses lead_assessor / assessor, and `certification_body` adds one
 * `technical_reviewer` (the certification decision maker, ISO/IEC 17021-1
 * §9.5.1.1) who never authors or reviews a workpaper (ADR-0081 §1).
 *
 * Objective-level work (workpapers, determinations) needs a program that
 * assesses by objectives (`readiness.objectiveMode === 'objectives'`); for one
 * that doesn't yet (SOC 2 until V106-1-5 / 1-9, ISO 42001 until V106-5 / 6-4)
 * it is 409 `assessor_objectives_not_declared`. Engagements, roles and the
 * independence flag work regardless. CCA work needs the
 * current Lead CCA's review. The lead may approve their own workpaper
 * (ADR-0067 owner decision 2): the determination records it
 * (`self_reviewed = 1`, migration 348) and the firm tier logs
 * `assessor.self_review`; another member may also review lead work.
 *
 * All queries are workspace-scoped; a row of another workspace or engagement
 * is `not_found`.
 */
import { generateRandomId } from '../utils/audit-crypto.js';
import { AssessmentDeterminationError, DETERMINATION_KINDS, normalizeEvidenceRefs, proposeDetermination, reviewDetermination, } from './determination-service.js';
import { getProgramManifest } from '../programs/manifests.js';
import { applicabilityScope, effectiveRequirementSource } from '../programs/requirement-source.js';
import { appliesToClass } from '../utils/class-applicability.js';
import { soaExclusions } from './soa-scope.js';
import { programRequirementIds } from './control-baseline-service.js';
import { ASSESSOR_FIRM_TYPE_SPECS } from './assessor-firm-types.js';
import { sqlIn } from '../utils/sql-in.js';
/** CMMC default (32 CFR 170.8(b)(17)(ii)(G)); the rule set may override it (content-only). */
export const C3PAO_INDEPENDENCE_LOOKBACK_MONTHS = 36;
/**
 * The lead and member role per assessor type (ADR-0059 §2, ADR-0067 §2,
 * ADR-0081 §1), plus the decision role where the family has one. Read from
 * the family specs (migration 533 seeds the same values).
 */
export const ASSESSOR_ROLE_VOCAB = Object.freeze(Object.fromEntries(Object.values(ASSESSOR_FIRM_TYPE_SPECS).map((s) => [s.type, {
        lead: s.lead, member: s.member, ...(s.decisionRole ? { decision: s.decisionRole } : {}),
    }])));
/** The determination kind an assessor type writes (ADR-0068 §2: `c3pao` stays its own kind). */
export function determinationKindFor(type) {
    return type === 'c3pao' ? 'c3pao' : 'assessor';
}
/**
 * True once the schema and the determination service accept kind `assessor`
 * (migration 335a + V103-6-5). Until then an assessor review of a
 * non-C3PAO engagement is refused with 409 `assessor_determinations_not_enabled`.
 */
export function assessorDeterminationsEnabled() {
    return DETERMINATION_KINDS.includes('assessor');
}
export class C3paoError extends Error {
    code;
    constructor(code, message) {
        super(message);
        this.code = code;
        this.name = 'C3paoError';
    }
}
// ---------------------------------------------------------------------------
// Roles
// ---------------------------------------------------------------------------
export async function listEngagementRoles(db, workspaceId, engagementId, opts = {}) {
    if (opts.atIso) {
        const r = await db.query(`SELECT * FROM assessor_engagement_roles
        WHERE workspace_id = ? AND engagement_id = ? AND role_start <= ? AND (role_end IS NULL OR role_end > ?)
        ORDER BY role_start, role_id`, [workspaceId, engagementId, opts.atIso, opts.atIso]);
        return r.results;
    }
    const r = await db.query(`SELECT * FROM assessor_engagement_roles WHERE workspace_id = ? AND engagement_id = ? ORDER BY role_start, role_id`, [workspaceId, engagementId]);
    return r.results;
}
/** The caller's open role on the engagement at `atIso` (lead first), or null. */
export async function userRoleOn(db, workspaceId, engagementId, userId, atIso) {
    const r = await db.query(`SELECT role FROM assessor_engagement_roles
      WHERE workspace_id = ? AND engagement_id = ? AND user_id = ?
        AND role_start <= ? AND (role_end IS NULL OR role_end > ?)
      ORDER BY CASE role WHEN 'lead_cca' THEN 0 WHEN 'lead_assessor' THEN 1 WHEN 'cca' THEN 2 ELSE 3 END
      LIMIT 1`, [workspaceId, engagementId, userId, atIso, atIso]);
    return r.results[0]?.role ?? null;
}
/**
 * Open a role. The firm tier has already proven the assignee is on this
 * engagement's roster (accepted firm staff, no client hat) and that the
 * assigner is the engagement partner / manager. Here: the role fits the
 * assessor type, one open role per person per engagement (a CCA can't also be
 * the Lead CCA, ADR-0059 §2), one open lead at a time.
 */
export async function assignEngagementRole(db, input) {
    const e = input.engagement;
    const vocab = ASSESSOR_ROLE_VOCAB[e.type];
    if (input.role !== vocab.lead && input.role !== vocab.member && (!vocab.decision || input.role !== vocab.decision)) {
        throw new C3paoError('invalid', vocab.decision
            ? `A ${e.type} engagement uses the roles ${vocab.lead}, ${vocab.member} and ${vocab.decision}.`
            : `A ${e.type} engagement uses the roles ${vocab.lead} and ${vocab.member}.`);
    }
    const at = (input.now ?? new Date()).toISOString();
    const open = await listEngagementRoles(db, e.workspaceId, e.engagementId, { atIso: at });
    if (open.some((r) => r.user_id === input.userId))
        throw new C3paoError('conflict', 'This person already holds a role on this engagement; end it first.');
    if (input.role === vocab.lead && open.some((r) => r.role === vocab.lead)) {
        throw new C3paoError('conflict', `This engagement already has a ${vocab.lead}; end that role first.`);
    }
    if (vocab.decision) {
        // ISO/IEC 17021-1 §9.5.1.1 (ADR-0081 §1): the decision maker is never one of the auditors, before or after.
        if (input.role === vocab.decision && open.some((r) => r.role === vocab.decision)) {
            throw new C3paoError('conflict', `This engagement already has a ${vocab.decision}; end that role first.`);
        }
        // T6-L6: over every engagement of this firm on this client period, not only this one.
        const scope = sqlIn(new Set([e.engagementId, ...(e.separationScopeEngagementIds ?? [])]));
        const history = (await db.query(`SELECT role FROM assessor_engagement_roles WHERE workspace_id = ? AND user_id = ? AND engagement_id IN ${scope.sql}`, [e.workspaceId, input.userId, ...scope.params])).results.map((r) => r.role);
        const worked = input.role === vocab.decision && (await db.query(`SELECT 1 AS x FROM cmmc_workpapers WHERE workspace_id = ? AND (author_user_id = ? OR reviewed_by_user_id = ?) AND engagement_id IN ${scope.sql} LIMIT 1`, [e.workspaceId, input.userId, input.userId, ...scope.params])).results.length > 0;
        if (input.role === vocab.decision ? worked || history.some((r) => r !== vocab.decision) : history.includes(vocab.decision)) {
            throw new C3paoError('sod', 'The certification decision maker is never an auditor of the same client period with this firm (ISO/IEC 17021-1 9.5.1.1).');
        }
    }
    const roleId = input.roleId ?? generateRandomId('erl');
    await db.execute(`INSERT INTO assessor_engagement_roles (role_id, workspace_id, engagement_id, user_id, role, role_start, assigned_by, created_at)
     VALUES (?, ?, ?, ?, ?, ?, ?, ?)`, [roleId, e.workspaceId, e.engagementId, input.userId, input.role, at, input.assignedBy, at]);
    return (await db.query(`SELECT * FROM assessor_engagement_roles WHERE role_id = ? AND workspace_id = ?`, [roleId, e.workspaceId])).results[0];
}
/** End an open role (append-only: the row keeps its window for independence reads). */
export async function endEngagementRole(db, input) {
    const e = input.engagement;
    const r = (await db.query(`SELECT * FROM assessor_engagement_roles WHERE role_id = ? AND workspace_id = ? AND engagement_id = ? LIMIT 1`, [input.roleId, e.workspaceId, e.engagementId])).results[0];
    if (!r)
        throw new C3paoError('not_found', 'Role not found on this engagement.');
    if (r.role_end)
        throw new C3paoError('conflict', 'This role has already ended.');
    let at = (input.now ?? new Date()).toISOString();
    if (at <= r.role_start)
        at = new Date(Date.parse(r.role_start) + 1).toISOString();
    const res = await db.execute(`UPDATE assessor_engagement_roles SET role_end = ? WHERE role_id = ? AND workspace_id = ? AND engagement_id = ? AND role_end IS NULL`, [at, input.roleId, e.workspaceId, e.engagementId]);
    if ((res.meta?.changes ?? 0) === 0)
        throw new C3paoError('conflict', 'This role has already ended.');
    return (await db.query(`SELECT * FROM assessor_engagement_roles WHERE role_id = ? AND workspace_id = ?`, [input.roleId, e.workspaceId])).results[0];
}
// ---------------------------------------------------------------------------
// Scope checks (H2: period / objective / asset of THIS engagement)
// ---------------------------------------------------------------------------
async function assertEngagementPeriod(db, e, periodId) {
    // The engagement's own period only: another period (this tenant's or another's) is not found.
    if (periodId !== e.periodId)
        throw new C3paoError('not_found', 'Audit period not found on this engagement.');
    const p = (await db.query(`SELECT status FROM audit_periods WHERE period_id = ? AND workspace_id = ? LIMIT 1`, [periodId, e.workspaceId])).results[0];
    if (!p)
        throw new C3paoError('not_found', 'Audit period not found on this engagement.');
    if (p.status === 'closed')
        throw new C3paoError('conflict', 'This period is closed; its determinations are final.');
}
/**
 * The objective must be an active objective of the period's program — its
 * framework, and (for a baseline-sourced program such as GovRAMP) the tier's
 * requirement set the period assesses (the Ready 80 for a Ready period).
 */
export async function assertObjectiveInPeriodScope(db, content, workspaceId, periodId, objectiveId) {
    const p = (await db.query(`SELECT c.program, c.profile_id, c.path, c.certification_class, COALESCE(p.program_options_json, c.program_options_json) AS program_options_json
       FROM audit_periods p JOIN certification_profiles c ON c.profile_id = p.profile_id AND c.workspace_id = p.workspace_id
      WHERE p.period_id = ? AND p.workspace_id = ? LIMIT 1`, [periodId, workspaceId])).results[0];
    if (!p)
        throw new C3paoError('not_found', 'Audit period not found on this engagement.');
    const manifest = getProgramManifest(p.program);
    if (!manifest?.frameworkId)
        throw new C3paoError('invalid', `The ${p.program} program does not assess by objectives.`);
    // ADR-0081 §3: objective-level assessor work needs a program that assesses by objectives. SOC 2 and
    // ISO 42001 declare theirs in V106-1-5 / V106-5 (objectiveMode 'objectives'); until then this is the state.
    if (manifest.readiness.objectiveMode !== 'objectives') {
        throw new C3paoError('assessor_objectives_not_declared', `The ${manifest.label} program doesn't assess by objectives yet.`);
    }
    let options = {};
    try {
        options = p.program_options_json ? JSON.parse(p.program_options_json) : {};
    }
    catch {
        options = {};
    }
    const columns = { path: p.path, certification_class: p.certification_class };
    // ADR-0079: the requirement source names the framework (a column-keyed source can pick another one).
    let frameworkId;
    try {
        frameworkId = effectiveRequirementSource(manifest, { ...columns, options }).frameworkId ?? manifest.frameworkId;
    }
    catch {
        throw new C3paoError('invalid', 'This period does not select a requirement set.');
    }
    const o = (await content.query(`SELECT o.requirement_id, r.applicability_json AS requirement_applicability
       FROM assessment_objectives o JOIN framework_requirements r ON r.requirement_id = o.requirement_id
      WHERE o.objective_id = ? AND o.status = 'active' AND r.status = 'active' AND r.framework_id = ? LIMIT 1`, [objectiveId, frameworkId])).results[0];
    if (!o)
        throw new C3paoError('invalid', `Objective ${objectiveId} is not an objective of this program.`);
    // ADR-0079 open question 1 (default yes; V106-6-4): the period's applicability applies to assessors too, the
    // same filters the objective engine applies (ADR-0068 §1 class applicability, ADR-0079 §7 option scope).
    if (manifest.readiness.classApplicability && p.certification_class && !appliesToClass(o.requirement_applicability, p.certification_class, false)) {
        throw new C3paoError('invalid', `Objective ${objectiveId} is outside this period's scope (class ${p.certification_class}).`);
    }
    const scope = applicabilityScope(manifest.readiness.applicability, options);
    let objectiveApplicability = null;
    if (scope) {
        // assessment_objectives.applicability_json arrives with the option-scoped programs (ADR-0079 §7); absent → none.
        try {
            objectiveApplicability = (await content.query(`SELECT applicability_json AS a FROM assessment_objectives WHERE objective_id = ? LIMIT 1`, [objectiveId])).results[0]?.a ?? null;
        }
        catch (e) {
            if (!/applicability_json/i.test(e.message ?? ''))
                throw e;
        }
    }
    if (scope && (!scope.requirement(o.requirement_applicability) || !scope.objective(objectiveApplicability))) {
        throw new C3paoError('invalid', `Objective ${objectiveId} is outside this period's scope.`);
    }
    // ADR-0080 §4 (V106-6-4): a control the approved Statement of Applicability excludes takes no determination.
    if (manifest.soa) {
        const soa = await soaExclusions(db, workspaceId, p.profile_id);
        if (soa?.excluded.includes(o.requirement_id)) {
            throw new C3paoError('invalid', `Objective ${objectiveId} belongs to a control the approved Statement of Applicability (v${soa.version}) excludes.`);
        }
    }
    let selected;
    try {
        selected = await programRequirementIds(content, manifest.requirementSource, options, columns);
    }
    catch {
        throw new C3paoError('invalid', 'This period does not select a requirement set.');
    }
    if (selected && !selected.has(o.requirement_id)) {
        throw new C3paoError('invalid', `Objective ${objectiveId} is outside the requirement set this period assesses.`);
    }
}
/**
 * V106-1-9 / ADR-0078 §8: the requirement or objective an assessor's audit exception names
 * (the firm-tier exception's control ref) must be in the period's scope, by the same filters as
 * determinations: a criterion ref (`CC6.1`), an objective ref (`CC6.1_obj.3`) or its label
 * (`CC6.1-POF-03`) of the period's program. Out of scope (another category, another program,
 * unknown) is `invalid` (422). A program that doesn't assess by objectives isn't checked
 * (returns null), so v9.8 firm-tier exceptions keep their free-text control refs.
 */
export async function assertRefInPeriodScope(db, content, workspaceId, periodId, ref) {
    const p = (await db.query(`SELECT c.program, COALESCE(p.program_options_json, c.program_options_json) AS program_options_json FROM audit_periods p JOIN certification_profiles c ON c.profile_id = p.profile_id AND c.workspace_id = p.workspace_id
      WHERE p.period_id = ? AND p.workspace_id = ? LIMIT 1`, [periodId, workspaceId])).results[0];
    const manifest = p ? getProgramManifest(p.program) : undefined;
    if (!p || !manifest?.frameworkId || manifest.readiness.objectiveMode !== 'objectives')
        return null;
    const fw = manifest.frameworkId;
    const obj = (await content.query(`SELECT o.objective_id FROM assessment_objectives o JOIN framework_requirements r ON r.requirement_id = o.requirement_id
      WHERE r.framework_id = ? AND o.status = 'active' AND (o.objective_id = ? OR o.label = ?) LIMIT 1`, [fw, `${fw}:${ref}`, ref])).results[0];
    if (obj) {
        await assertObjectiveInPeriodScope(db, content, workspaceId, periodId, obj.objective_id);
        return { kind: 'objective', id: obj.objective_id };
    }
    const req = (await content.query(`SELECT requirement_id, applicability_json FROM framework_requirements WHERE framework_id = ? AND requirement_id = ? AND status = 'active' LIMIT 1`, [fw, `${fw}:${ref}`])).results[0];
    if (!req)
        throw new C3paoError('invalid', `${ref} is not a requirement or objective of the ${manifest.label} program.`);
    let options = {};
    try {
        options = p.program_options_json ? JSON.parse(p.program_options_json) : {};
    }
    catch {
        options = {};
    }
    const scope = applicabilityScope(manifest.readiness.applicability, options);
    if (scope && !scope.requirement(req.applicability_json))
        throw new C3paoError('invalid', `${ref} is outside this period's scope.`);
    return { kind: 'requirement', id: req.requirement_id };
}
/** A live, non-retired asset in this period's scope; anything else is not found. */
async function assertAssetInPeriodScope(db, workspaceId, periodId, assetId) {
    const r = await db.query(`SELECT 1 AS ok FROM asset_period_memberships m JOIN assets a ON a.asset_id = m.asset_id AND a.workspace_id = m.workspace_id
      WHERE m.workspace_id = ? AND m.period_id = ? AND m.asset_id = ? AND m.removed_at IS NULL AND a.retired_at IS NULL LIMIT 1`, [workspaceId, periodId, assetId]);
    if (!r.results.length)
        throw new C3paoError('not_found', 'Asset not found in this period\'s scope.');
}
function rethrow(e) {
    if (e instanceof AssessmentDeterminationError) {
        const code = e.code === 'ineligible' ? 'invalid' : e.code;
        throw new C3paoError(code, e.message);
    }
    throw e;
}
/** Legacy rows stored bare artifact ids; new rows store typed refs. */
export function parseWorkpaperRefs(json) {
    let raw;
    try {
        raw = JSON.parse(json);
    }
    catch {
        return [];
    }
    if (!Array.isArray(raw))
        return [];
    return raw.flatMap((r) => {
        if (typeof r === 'string')
            return [{ kind: 'artifact', id: r }];
        if (r && typeof r === 'object' && typeof r.id === 'string' && typeof r.kind === 'string')
            return [r];
        return [];
    });
}
export async function createWorkpaper(db, input) {
    const e = input.engagement;
    const content = input.contentDb ?? db;
    const nowIso = (input.now ?? new Date()).toISOString();
    if (!['examine', 'interview', 'test'].includes(input.method))
        throw new C3paoError('invalid', 'method must be examine, interview or test.');
    await assertEngagementPeriod(db, e, input.periodId);
    await assertObjectiveInPeriodScope(db, content, e.workspaceId, input.periodId, input.objectiveId);
    const assetId = input.assetId ?? null;
    if (assetId)
        await assertAssetInPeriodScope(db, e.workspaceId, input.periodId, assetId);
    let refs;
    try {
        refs = await normalizeEvidenceRefs(db, e.workspaceId, input.evidenceRefs ?? []);
    }
    catch (err) {
        rethrow(err);
    }
    const vocab = ASSESSOR_ROLE_VOCAB[e.type];
    const authorRole = await userRoleOn(db, e.workspaceId, e.engagementId, input.authorUserId, nowIso);
    if (authorRole !== vocab.lead && authorRole !== vocab.member) {
        throw new C3paoError('forbidden', `Author must hold an open ${vocab.lead} or ${vocab.member} role on this engagement.`);
    }
    const workpaperId = input.workpaperId ?? generateRandomId('wpc');
    try {
        await db.execute(`INSERT INTO cmmc_workpapers (cmmc_workpaper_id, workspace_id, engagement_id, period_id, objective_id, asset_id, method, evidence_refs_json, author_user_id, author_role, authored_at)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`, [workpaperId, e.workspaceId, e.engagementId, input.periodId, input.objectiveId, assetId, input.method, JSON.stringify(refs), input.authorUserId, authorRole, nowIso]);
    }
    catch (err) {
        if (/UNIQUE|unique/.test(err.message)) {
            throw new C3paoError('conflict', 'An open workpaper already exists for this objective and asset on this engagement.');
        }
        throw err;
    }
    return (await getWorkpaper(db, e.workspaceId, workpaperId));
}
export async function getWorkpaper(db, workspaceId, workpaperId) {
    const r = await db.query(`SELECT * FROM cmmc_workpapers WHERE cmmc_workpaper_id = ? AND workspace_id = ? LIMIT 1`, [workpaperId, workspaceId]);
    return r.results[0] ?? null;
}
export async function listWorkpapers(db, workspaceId, opts = {}) {
    const clauses = ['workspace_id = ?'];
    const params = [workspaceId];
    if (opts.engagementId) {
        clauses.push('engagement_id = ?');
        params.push(opts.engagementId);
    }
    if (opts.periodId) {
        clauses.push('period_id = ?');
        params.push(opts.periodId);
    }
    const r = await db.query(`SELECT * FROM cmmc_workpapers WHERE ${clauses.join(' AND ')} ORDER BY authored_at DESC, cmmc_workpaper_id`, params);
    return r.results;
}
/** A review claim older than this is abandoned (a crashed request) and may be taken over. */
const REVIEW_CLAIM_TTL_MS = 5 * 60 * 1000;
/**
 * Review a workpaper. The reviewer claims the workpaper atomically
 * (`result IS NULL` guard), then the determination is proposed (author as
 * proposer) and reviewed (reviewer) through the determination service, which
 * supersedes the cell's previous reviewed row in the same batch. A lost race
 * is a 409; a failed write releases the claim.
 */
export async function reviewWorkpaper(db, input) {
    const e = input.engagement;
    const now = input.now ?? new Date();
    const nowIso = now.toISOString();
    const wp = await getWorkpaper(db, e.workspaceId, input.workpaperId);
    // H1: the workpaper must be THIS engagement's (the URL's), else not found.
    if (!wp || wp.engagement_id !== e.engagementId)
        throw new C3paoError('not_found', 'Workpaper not found.');
    if (wp.result !== null)
        throw new C3paoError('conflict', 'Workpaper is already reviewed; open a new workpaper for the same objective to supersede it.');
    if (!['met', 'not_met', 'na'].includes(input.result))
        throw new C3paoError('invalid', 'result must be met, not_met or na.');
    const rationaleIn = input.rationale?.trim() || null;
    if (input.result === 'not_met' && !rationaleIn)
        throw new C3paoError('invalid', 'A Not met result requires a rationale.');
    if (input.result !== 'not_met' && input.gapType)
        throw new C3paoError('invalid', 'A gap type is only for a Not met result.');
    await assertEngagementPeriod(db, e, wp.period_id);
    const vocab = ASSESSOR_ROLE_VOCAB[e.type];
    const reviewerRole = await userRoleOn(db, e.workspaceId, e.engagementId, input.reviewerUserId, nowIso);
    if (!reviewerRole)
        throw new C3paoError('forbidden', 'Reviewer must hold an open role on this engagement.');
    // ADR-0067 owner decision 2: the lead may approve their own workpaper (logged + flagged); anyone else needs a second person.
    const selfReview = wp.author_user_id === input.reviewerUserId;
    if (selfReview && (wp.author_role !== vocab.lead || reviewerRole !== vocab.lead)) {
        throw new C3paoError('sod', `The reviewer cannot be the author (only the ${vocab.lead} may approve their own work).`);
    }
    if (wp.author_role === vocab.member && reviewerRole !== vocab.lead) {
        throw new C3paoError('sod', `${vocab.member} work needs the current ${vocab.lead}'s review.`);
    }
    if (wp.author_role === vocab.lead && reviewerRole !== vocab.lead && reviewerRole !== vocab.member) {
        throw new C3paoError('forbidden', `Reviewer role must be ${vocab.lead} or ${vocab.member}.`);
    }
    const kind = determinationKindFor(e.type);
    if (kind === 'assessor' && !assessorDeterminationsEnabled()) {
        throw new C3paoError('assessor_determinations_not_enabled', 'assessor determinations not enabled');
    }
    // ADR-0068 §2: an `assessor` row carries the engagement family as its firm type (govramp_3pao / fedramp_recognized).
    const firmCtx = kind === 'assessor' ? { firmType: e.type } : {};
    // ADR-0081 §3 (V106-6-4): a certification body's Not met carries the ISO/IEC 17021-1 grade; nothing else does.
    const grade = input.nonconformityGrade ?? null;
    if (grade !== null && grade !== 'major' && grade !== 'minor')
        throw new C3paoError('invalid', 'nonconformity_grade must be major or minor.');
    const cbNotMet = e.type === 'certification_body' && input.result === 'not_met';
    if (cbNotMet && !grade)
        throw new C3paoError('invalid', 'A certification body\'s Not met needs nonconformity_grade (major or minor).');
    if (!cbNotMet && grade)
        throw new C3paoError('invalid', 'nonconformity_grade belongs only to a certification body\'s Not met.');
    // Claim (H2: two reviewers can't both write a determination).
    const stale = new Date(now.getTime() - REVIEW_CLAIM_TTL_MS).toISOString();
    const claim = await db.execute(`UPDATE cmmc_workpapers SET reviewed_by_user_id = ?, reviewed_at = ?
      WHERE cmmc_workpaper_id = ? AND workspace_id = ? AND engagement_id = ? AND result IS NULL
        AND (reviewed_by_user_id IS NULL OR reviewed_at < ?)`, [input.reviewerUserId, nowIso, wp.cmmc_workpaper_id, e.workspaceId, e.engagementId, stale]);
    if ((claim.meta?.changes ?? 0) === 0)
        throw new C3paoError('conflict', 'Another review of this workpaper is in progress or done; reload it.');
    const release = () => db.execute(`UPDATE cmmc_workpapers SET reviewed_by_user_id = NULL, reviewed_at = NULL
      WHERE cmmc_workpaper_id = ? AND workspace_id = ? AND reviewed_by_user_id = ? AND reviewed_at = ? AND result IS NULL`, [wp.cmmc_workpaper_id, e.workspaceId, input.reviewerUserId, nowIso]).catch(() => undefined);
    // Determinations know `met / not_met / not_applicable / no_evidence`; a reviewed assessor row is never no_evidence (ADR-0059 §4).
    const detResult = input.result === 'na' ? 'not_applicable' : input.result;
    const rationale = detResult === 'met' ? null : (rationaleIn ?? `${e.type} reviewed as ${input.result}`);
    try {
        const proposed = await proposeDetermination(db, {
            workspaceId: e.workspaceId, periodId: wp.period_id, objectiveId: wp.objective_id, assetId: wp.asset_id,
            kind: kind, result: detResult, gapType: detResult === 'not_met' ? (input.gapType ?? 'not_implemented') : null,
            rationale, evidenceRefs: parseWorkpaperRefs(wp.evidence_refs_json),
            proposedBy: wp.author_user_id, assessorContext: { engagementId: e.engagementId, userId: wp.author_user_id, ...firmCtx },
            nonconformityGrade: grade, contentDb: input.contentDb, now,
        });
        if (proposed.assessor_engagement_id !== e.engagementId || proposed.proposed_by !== wp.author_user_id) {
            throw new C3paoError('conflict', 'Another engagement has an open proposal for this objective; retry.');
        }
        const reviewed = await reviewDetermination(db, {
            workspaceId: e.workspaceId, determinationId: proposed.determination_id, reviewedBy: input.reviewerUserId,
            assessorContext: { engagementId: e.engagementId, userId: input.reviewerUserId, ...firmCtx }, contentDb: input.contentDb, now, selfReview,
        });
        const fin = await db.execute(`UPDATE cmmc_workpapers SET result = ?, rationale = ?, determination_id = ?
        WHERE cmmc_workpaper_id = ? AND workspace_id = ? AND reviewed_by_user_id = ? AND result IS NULL`, [input.result, rationale, reviewed.determination_id, wp.cmmc_workpaper_id, e.workspaceId, input.reviewerUserId]);
        if ((fin.meta?.changes ?? 0) === 0)
            throw new C3paoError('conflict', 'The workpaper changed while reviewing; reload it.');
    }
    catch (err) {
        await release();
        if (err instanceof C3paoError)
            throw err;
        rethrow(err);
    }
    return (await getWorkpaper(db, e.workspaceId, wp.cmmc_workpaper_id));
}
//# sourceMappingURL=c3pao-service.js.map